import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Auth guards
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Layouts
import AdminLayout from '../layouts/AdminLayout/AdminLayout';
import DashboardLayout from '../layouts/DashboardLayout/DashboardLayout';

// Hooks
import useAdminUser from '../hooks/useAdminUser';

// Route constants
import { USER_ROUTES, ADMIN_ROUTES } from '../constants/routeConstants';

// Components
import { LoadingSpinner } from '../components';

// ─── Lazy-loaded Pages (code-split per route) ───────────────
const UserLogin = lazy(() => import('../pages/UserLogin/UserLogin'));
const AdminLogin = lazy(() => import('../pages/AdminLogin/AdminLogin'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword/ResetPassword'));

const UserDashboard = lazy(() => import('../pages/UserDashboard/UserDashboard'));
const SearchResults = lazy(() => import('../pages/SearchResults/SearchResults'));
const UserProfile = lazy(() => import('../pages/UserProfile/UserProfile'));
const UserHistory = lazy(() => import('../pages/UserHistory/UserHistory'));
const SupportTickets = lazy(() => import('../pages/UserSupportTickets/UserSupportTickets'));
const UserTicketDetail = lazy(() => import('../pages/UserTicketDetail/UserTicketDetail'));

const AdminDashboard = lazy(() => import('../pages/AdminDashboard/AdminDashboard'));
const AdminTickets = lazy(() => import('../pages/AdminTickets/AdminTickets'));
const AdminTicketDetail = lazy(() => import('../pages/AdminTicketDetail/AdminTicketDetail'));
const AdminUsers = lazy(() => import('../pages/AdminUsers/AdminUsers'));
const AssignTickets = lazy(() => import('../pages/AdminAssignTickets/AdminAssignTickets'));
const Employees = lazy(() => import('../pages/AdminEmployees/AdminEmployees'));

// ─── Route Wrappers ─────

/** Wraps a user page in auth guard + dashboard layout. */
const UserRoute = ({ children, withContainer = false }) => (
  <ProtectedRoute type="user">
    <DashboardLayout>
      {withContainer ? <div className="container mt-4">{children}</div> : children}
    </DashboardLayout>
  </ProtectedRoute>
);

/** Wraps an admin page in auth guard + admin layout. Uses render-prop children(user) for pages that need user. */
const AdminRoute = ({ children, requiredRole }) => {
  const { adminUser } = useAdminUser();
  return (
    <ProtectedRoute type="admin" requiredRole={requiredRole}>
      <AdminLayout>
        {typeof children === 'function' ? children(adminUser) : children}
      </AdminLayout>
    </ProtectedRoute>
  );
};

// ─── App Routes ──────────

const AppRoutes = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <Routes>
      {/* ── Public ──────────────────────────────────── */}
      <Route path={USER_ROUTES.LOGIN} element={
        <PublicRoute type="user"><UserLogin /></PublicRoute>
      } />
      <Route path={ADMIN_ROUTES.LOGIN} element={
        <PublicRoute type="admin"><AdminLogin /></PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute type="user"><ForgotPassword /></PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute type="user"><ResetPassword /></PublicRoute>
      } />

      {/* ── User ────────────────────────────────────── */}
      <Route path={USER_ROUTES.DASHBOARD} element={
        <UserRoute><UserDashboard /></UserRoute>
      } />
      <Route path={USER_ROUTES.SEARCH_RESULTS} element={
        <UserRoute><SearchResults /></UserRoute>
      } />
      <Route path={USER_ROUTES.PROFILE} element={
        <UserRoute withContainer><UserProfile /></UserRoute>
      } />
      <Route path={USER_ROUTES.HISTORY} element={
        <UserRoute withContainer><UserHistory /></UserRoute>
      } />
      <Route path={USER_ROUTES.SUPPORT} element={
        <UserRoute withContainer><SupportTickets /></UserRoute>
      } />
      <Route path={USER_ROUTES.TICKET_DETAIL} element={
        <UserRoute><UserTicketDetail /></UserRoute>
      } />

      {/* ── Admin ───────────────────────────────────── */}
      <Route path={ADMIN_ROUTES.DASHBOARD} element={
        <AdminRoute>{(user) => <AdminDashboard user={user} />}</AdminRoute>
      } />
      <Route path={ADMIN_ROUTES.TICKETS} element={
        <AdminRoute>{(user) => <AdminTickets user={user} />}</AdminRoute>
      } />
      <Route path={ADMIN_ROUTES.TICKET_DETAIL} element={
        <AdminRoute>{(user) => <AdminTicketDetail user={user} />}</AdminRoute>
      } />
      <Route path={ADMIN_ROUTES.USERS} element={
        <AdminRoute>{(user) => <AdminUsers user={user} />}</AdminRoute>
      } />
      <Route path={ADMIN_ROUTES.ASSIGN} element={
        <AdminRoute requiredRole="admin"><AssignTickets /></AdminRoute>
      } />
      <Route path={ADMIN_ROUTES.EMPLOYEES} element={
        <AdminRoute requiredRole="admin">{(user) => <Employees user={user} />}</AdminRoute>
      } />

      {/* ── Redirects ───────────────────────────────── */}
      <Route path="/" element={<Navigate to={USER_ROUTES.DASHBOARD} replace />} />
      <Route path="/admin" element={<Navigate to={ADMIN_ROUTES.DASHBOARD} replace />} />
      <Route path="*" element={<Navigate to={USER_ROUTES.DASHBOARD} replace />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
